import type { RefObject } from 'preact';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { useFocusWithin } from '../UNSAFE_useFocusWithin';

export type FocusableHandle = {
  focus: () => void;
  blur: () => void;
};
type UseFocusWithinProps = Parameters<typeof useFocusWithin>[0];
type UseFocusableTextFieldProps<E, R> = UseFocusWithinProps & {
  /**
   * flag indicating whether the field is readonly
   */
  isReadonly?: boolean;

  /**
   * ref to enabled element
   */
  enabledElementRef: RefObject<E>;

  /**
   * ref to readonly element
   */
  readonlyElementRef: RefObject<R>;
};

/**
 * A custom hook that handles the focus when the text field
 * is toggled between readonly and enabled
 * @typedef E represents the type of the enabled element
 * @typedef R represents the type of the readonly element
 */
export function useFocusableTextField<E extends HTMLElement, R extends HTMLElement>({
  isReadonly,
  enabledElementRef,
  readonlyElementRef,
  ...useFocusWithinProps
}: UseFocusableTextFieldProps<E, R>) {
  const { focusProps, isFocused } = useFocusWithin(useFocusWithinProps);
  const previousIsFocusedRef = useRef(isFocused);

  // Retain focus when toggling between readonly and enabled
  useEffect(() => {
    // if the readonly state is changed and previously we
    // were holding focus, set the focus to newly rendered element
    if (previousIsFocusedRef.current) {
      if (isReadonly) {
        setTimeout(() => readonlyElementRef.current?.focus());
      } else {
        setTimeout(() => enabledElementRef.current?.focus());
      }
    }
  }, [enabledElementRef, isReadonly, readonlyElementRef]);

  // Keep the previousFocusRef in sync, but be sure to run this hook
  // after handling focus for the readonly toggling.
  useEffect(() => {
    previousIsFocusedRef.current = isFocused;
  }, [isFocused]);

  const methods = useMemo(
    () => ({
      focus: () => {
        if (isReadonly) {
          readonlyElementRef.current?.focus();
        } else {
          enabledElementRef.current?.focus();
        }
      },
      blur: () => {
        if (isReadonly) {
          readonlyElementRef.current?.blur();
        } else {
          enabledElementRef.current?.blur();
        }
      }
    }),
    [enabledElementRef, isReadonly, readonlyElementRef]
  );

  return {
    isFocused,
    focusProps,
    methods
  };
}
